(ns hai.notes.detail
  "Full-page view of a single note as the shared editor surface — the same
   one the edit/create modals show: auto-saving text, tag/link/contact
   sections, the save/share footer, and the header ⋮ holding Delete."
  (:require
   [clojure.string :as str]
   [hai.entity.data :as entity-data]
   [hai.entity.staging :as staging]
   [hai.sharing.helper :as sharing]
   [hai.notes.data :as data]
   [hai.notes.editor :as editor]
   [hai.ui.components :as c]
   [hai.ui.components.read-only-cue :as read-only-cue]
   [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [hai.web-app.viewport :as viewport]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(defn- note-label
  "First non-empty line of a note, truncated. Falls back to \"Note\" for an
   empty or not-yet-loaded note."
  [note]
  (let [first-line (some->> (:note/text note)
                            str/split-lines
                            (map str/trim)
                            (some not-empty))]
    (cond
      (nil? first-line)         "Note"
      (> (count first-line) 60) (str (subs first-line 0 60) "…")
      :else                     first-line)))

(defsub ::window-title
  [db]
  (let [note-uuid (get-in db [:routing/current-route :path-params :note/uuid])
        note (when note-uuid (get-in db [:note/uuid (uuid (str note-uuid))]))]
    (note-label note)))

;; Tracks which notes a resolve has completed for, so the page can tell
;; "still loading" apart from "not found / no access" — both leave a nil entity.
(defevent ::mark-resolved [db note-uuid]
  (update db ::resolved-uuids (fnil conj #{}) (uuid (str note-uuid))))

(defsub ::resolved?
  [db note-uuid]
  (boolean (and note-uuid
                (contains? (get db ::resolved-uuids #{}) (uuid (str note-uuid))))))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

;; The dialog caps the modal surface at 900px and floats it over a dimmed
;; page; here the column mirrors the cap and a shadow provides the elevation
;; the overlay would.
(o/defstyled editor-column :div
  {:max-width "900px"}
  [editor/edit-modal
   {:box-shadow "0 1px 2px rgba(45,62,42,0.08), 0 12px 32px rgba(45,62,42,0.16)"}])

(o/defstyled muted :span
  {:color t/--card-forest-70 :font-size "14px"})

(o/defstyled loading-state :div
  :flex :items-center
  {:gap "10px" :padding "8px 2px" :color t/--card-forest-70})

;; ---------------------------------------------------------------------------
;; View
;; ---------------------------------------------------------------------------

(defn- note-view [note]
  (r/with-let [!autosave (atom nil)]
    (let [note-uuid         (:note/uuid note)
          can-edit?         (sharing/can-edit? note)
          can-delete?       (sharing/can-delete? note)
          staged-links      @(<s [::staging/staged note-uuid :links])
          staged-tags       @(<s [::staging/staged note-uuid :tags])
          staged-contacts   @(<s [::staging/staged note-uuid :contacts])
          staged-notes      @(<s [::staging/staged note-uuid :notes])
          staged-tasks      @(<s [::staging/staged note-uuid :tasks])
          removed-links     @(<s [::staging/removals note-uuid :links])
          removed-tags      @(<s [::staging/removals note-uuid :tags])
          removed-contacts  @(<s [::staging/removals note-uuid :contacts])
          removed-notes     @(<s [::staging/removals note-uuid :notes])
          removed-tasks     @(<s [::staging/removals note-uuid :tasks])
          link-urls      (-> (->> (:note/activities note)
                                  (keep :activity/url))
                             (staging/merge-staged identity staged-links removed-links))
          tags           (-> (:note/tags note)
                             (staging/merge-staged-tags staged-tags removed-tags))
          contacts       (-> (:note/contacts note)
                             (staging/merge-staged :contact/uuid staged-contacts removed-contacts))
          linked-notes   (-> (:note/notes note)
                             (staging/merge-staged :note/uuid staged-notes removed-notes))
          linked-tasks   (-> (:note/tasks note)
                             (staging/merge-staged :task/uuid staged-tasks removed-tasks))
          delete! (fn []
                    ;; Drop the pending autosave so the unmount flush can't
                    ;; re-save the note being deleted.
                    (when-let [{:keys [discard!]} @!autosave]
                      (discard!))
                    (>e [::data/delete note-uuid])
                    (>e [:routing/navigate [:notes/view]]))
          mobile? @(<s [::viewport/mobile?])
          editor-props
          {:title           (if can-edit? "Editing note" "Note")
           :note            note
           :tags            tags
           :link-urls       link-urls
           :contacts        contacts
           :linked-notes    linked-notes
           :linked-tasks    linked-tasks
           :editing?        can-edit?
           :share?          can-edit?
           :auto-focus?     false
           :form-id         [::edit-form note-uuid]
           :!autosave       !autosave
           :on-save-text    (fn [text]
                              (>e [::data/save {:note/uuid note-uuid :note/text text}]))
           :extras-handlers
           {:on-add-tag        (fn [tag] (>e [::data/add-tag {:note/uuid note-uuid :tag tag}]))
            :on-remove-tag     (fn [tag]
                                 (if (:group/uuid tag)
                                   (>e [::data/remove-tag {:note/uuid note-uuid :group/uuid (:group/uuid tag)}])
                                   ;; ✕ on a chip still awaiting its create —
                                   ;; retire it locally; the create's response
                                   ;; restores server truth either way.
                                   (>e [::staging/unstage note-uuid :tags tag])))
            :on-add-link       (fn [link-url] (>e [::data/add-link {:note/uuid note-uuid :url link-url}]))
            :on-remove-link    (fn [link-url] (>e [::data/remove-link {:note/uuid note-uuid :url link-url}]))
            :on-add-contact    (fn [contact] (>e [::data/add-contact {:note/uuid note-uuid :contact contact}]))
            :on-remove-contact (fn [contact-uuid] (>e [::data/remove-contact {:note/uuid note-uuid :contact/uuid contact-uuid}]))
            :on-add-note       (fn [linked-note] (>e [::data/add-note-link {:note/uuid note-uuid :note linked-note}]))
            :on-remove-note    (fn [linked-uuid] (>e [::data/remove-note-link {:note/uuid note-uuid :note/linked-uuid linked-uuid}]))
            :on-add-task       (fn [task] (>e [::data/add-task {:note/uuid note-uuid :task task}]))
            :on-remove-task    (fn [task-uuid] (>e [::data/remove-task {:note/uuid note-uuid :task/uuid task-uuid}]))
            :on-open-entity    entity-data/open-entity}
           :on-share-closed #(>e [:entity/resolve {:note/uuid note-uuid}])
           :on-close        #(>e [:routing/navigate [:notes/view]])
           :on-delete       (when can-delete? delete!)}]
      (if mobile?
        ;; The same full-screen surface the home card expands into; ✕ returns
        ;; to the wall.
        [editor/screen editor-props]
        [editor-column
         (when-not can-edit?
           [read-only-cue/cue
            (if can-delete?
              "You can delete this note, but not edit it"
              "This note is read-only")])
         [editor/modal-content editor-props]]))))

(defn page [_route]
  (let [params    @(<s [:routing/path-params])
        note      @(<s [:entity params])
        resolved? @(<s [::resolved? (:note/uuid params)])]
    [layout/page
     [layout/content
      (cond
        note      ^{:key (:note/uuid note)} [note-view note]
        resolved? [muted "This note doesn't exist or you no longer have access to it."]
        :else     [loading-state [c/Spinner {:size "3"}] "Loading…"])]]))
